import { FormEvent, useEffect, useMemo, useState } from "react";
import { FounderConfirmPublish } from "../../../components/founder/FounderConfirmPublish";
import { FounderIconButton } from "../../../components/founder/FounderIconButton";
import { FounderOperationalShell } from "../../../components/founder/FounderOperationalShell";
import {
  FounderEmptyState,
  FounderErrorState,
  FounderLoadingState,
} from "../../../components/founder/FounderWorkspaceChrome";
import {
  createFounderCategory,
  fetchFounderCategories,
  fetchFounderWorkspaceCounts,
  updateFounderCategory,
  type FounderCategoryRow,
} from "../../../founder/founderWorkspaceApi";

type CategoryDraft = {
  name: string;
  displayOrder: string;
  isActive: boolean;
};

function toCategoryDraft(category: FounderCategoryRow): CategoryDraft {
  return {
    name: category.name,
    displayOrder: String(category.display_order),
    isActive: category.is_active && !category.archived_at,
  };
}

export function FounderMarketplaceWorkspace() {
  const [counts, setCounts] = useState<Awaited<ReturnType<typeof fetchFounderWorkspaceCounts>> | null>(null);
  const [categories, setCategories] = useState<FounderCategoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [categoryDraft, setCategoryDraft] = useState<CategoryDraft | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [workspaceCounts, categoryRows] = await Promise.all([
        fetchFounderWorkspaceCounts(),
        fetchFounderCategories(),
      ]);
      setCounts(workspaceCounts);
      setCategories(categoryRows);
      setError("");
    } catch {
      setError("Could not load marketplace data.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const editingCategory = categories.find((row) => row.id === editingCategoryId) ?? null;
  const categoryDirty = useMemo(() => {
    if (!editingCategory || !categoryDraft) {
      return false;
    }
    const original = toCategoryDraft(editingCategory);
    return (
      original.name !== categoryDraft.name ||
      original.displayOrder !== categoryDraft.displayOrder ||
      original.isActive !== categoryDraft.isActive
    );
  }, [categoryDraft, editingCategory]);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) {
      return;
    }
    setCreating(true);
    setMessage("");
    try {
      await createFounderCategory({ name: name.trim() });
      setName("");
      setMessage("Category published.");
      await load();
    } catch {
      setMessage("Could not create category.");
    } finally {
      setCreating(false);
    }
  }

  function startEditCategory(category: FounderCategoryRow) {
    setEditingCategoryId(category.id);
    setCategoryDraft(toCategoryDraft(category));
    setConfirmPublish(false);
  }

  function cancelEditCategory() {
    setEditingCategoryId(null);
    setCategoryDraft(null);
    setConfirmPublish(false);
  }

  async function publishCategoryUpdate() {
    if (!editingCategoryId || !categoryDraft || !editingCategory) {
      return;
    }
    const displayOrder = Number(categoryDraft.displayOrder);
    if (!categoryDraft.name.trim() || Number.isNaN(displayOrder)) {
      setMessage("Enter a valid category name and display order.");
      return;
    }

    setSubmitting(true);
    setMessage("");
    try {
      const wasArchived = Boolean(editingCategory.archived_at);
      const updated = await updateFounderCategory(editingCategoryId, {
        name: categoryDraft.name.trim(),
        displayOrder,
        isActive: categoryDraft.isActive,
        archive: wasArchived && categoryDraft.isActive ? false : undefined,
      });
      setCategories((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      setMessage("Category updated.");
      cancelEditCategory();
    } catch {
      setMessage("Could not update category.");
    } finally {
      setSubmitting(false);
      setConfirmPublish(false);
    }
  }

  async function archiveCategory() {
    if (!editingCategoryId) {
      return;
    }
    setSubmitting(true);
    setMessage("");
    try {
      const updated = await updateFounderCategory(editingCategoryId, { archive: true });
      setCategories((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      setMessage("Category archived.");
      cancelEditCategory();
    } catch {
      setMessage("Could not archive category.");
    } finally {
      setSubmitting(false);
      setConfirmPublish(false);
    }
  }

  const metrics = [
    { id: "cats", label: "Categories", value: categories.length, tone: "steel" as const },
    { id: "apps", label: "Published apps", value: counts?.publishedApplications ?? "—", tone: "mint" as const },
    { id: "pending", label: "Pending reviews", value: counts?.applicationsPendingReview ?? "—", tone: "coral" as const },
  ];

  return (
    <FounderOperationalShell metrics={metrics} title="Marketplace">
      {loading ? <FounderLoadingState /> : null}
      {!loading && error ? <FounderErrorState message={error} /> : null}

      {!loading && !error ? (
        <div className="oac-founder-ops-grid">
          <section className="oac-founder-ops-panel">
            <h2 className="oac-founder-ops-panel__title">Add category</h2>
            <form className="oac-founder-ops-inline-form" onSubmit={handleCreate}>
              <input
                aria-label="Category name"
                onChange={(e) => setName(e.target.value)}
                placeholder="Category name"
                required
                value={name}
              />
              <button
                className="oac-founder-btn oac-founder-btn--primary oac-founder-btn--with-icon"
                disabled={creating}
                type="submit"
              >
                {creating ? "Publishing..." : "Publish category"}
              </button>
            </form>
            {message ? <p className="oac-founder-ops-detail__result">{message}</p> : null}
          </section>

          <section className="oac-founder-ops-panel oac-founder-ops-panel--wide">
            <h2 className="oac-founder-ops-panel__title">Categories</h2>
            {categories.length === 0 ? (
              <FounderEmptyState message="No categories yet." />
            ) : (
              <div className="oac-founder-table-wrap">
                <table className="oac-founder-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Order</th>
                      <th>Status</th>
                      <th aria-label="Actions"> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((category) => (
                      <tr
                        className={editingCategoryId === category.id ? "oac-founder-table__row--active" : ""}
                        key={category.id}
                      >
                        <td>{category.name}</td>
                        <td>{category.display_order}</td>
                        <td>
                          <span
                            className={
                              category.is_active && !category.archived_at
                                ? "oac-founder-badge oac-founder-badge--healthy"
                                : "oac-founder-badge oac-founder-badge--neutral"
                            }
                          >
                            {category.archived_at ? "Archived" : category.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <FounderIconButton
                            kind="edit"
                            label={`Edit ${category.name}`}
                            onClick={() => startEditCategory(category)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {editingCategory && categoryDraft ? (
              <div className="oac-founder-ops-edit-panel">
                <h3 className="oac-founder-ops-edit-panel__title">Edit {editingCategory.name}</h3>
                <div className="oac-founder-field">
                  <label htmlFor="founder-category-edit-name">Name</label>
                  <input
                    id="founder-category-edit-name"
                    onChange={(e) => setCategoryDraft({ ...categoryDraft, name: e.target.value })}
                    value={categoryDraft.name}
                  />
                </div>
                <div className="oac-founder-field">
                  <label htmlFor="founder-category-edit-order">Display order</label>
                  <input
                    id="founder-category-edit-order"
                    inputMode="numeric"
                    onChange={(e) => setCategoryDraft({ ...categoryDraft, displayOrder: e.target.value })}
                    value={categoryDraft.displayOrder}
                  />
                </div>
                <label className="oac-founder-ops-radio">
                  <input
                    checked={categoryDraft.isActive}
                    onChange={(e) => setCategoryDraft({ ...categoryDraft, isActive: e.target.checked })}
                    type="checkbox"
                  />
                  Category active
                </label>
                {categoryDirty ? (
                  <div className="oac-founder-actions">
                    <button
                      className="oac-founder-btn oac-founder-btn--primary"
                      onClick={() => setConfirmPublish(true)}
                      type="button"
                    >
                      Publish update
                    </button>
                    <button className="oac-founder-btn oac-founder-btn--secondary" onClick={cancelEditCategory} type="button">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="oac-founder-actions">
                    <button className="oac-founder-btn oac-founder-btn--secondary" onClick={cancelEditCategory} type="button">
                      Close
                    </button>
                    {!editingCategory.archived_at ? (
                      <button
                        className="oac-founder-btn oac-founder-btn--danger"
                        onClick={() => setConfirmPublish(true)}
                        type="button"
                      >
                        Archive
                      </button>
                    ) : null}
                  </div>
                )}
                {confirmPublish ? (
                  <FounderConfirmPublish
                    message={
                      categoryDirty
                        ? `Publish changes to ${categoryDraft.name}?`
                        : `Archive ${categoryDraft.name}?`
                    }
                    onCancel={() => setConfirmPublish(false)}
                    onConfirm={categoryDirty ? publishCategoryUpdate : archiveCategory}
                    submitting={submitting}
                  />
                ) : null}
              </div>
            ) : null}
          </section>
        </div>
      ) : null}
    </FounderOperationalShell>
  );
}
